import { Link } from 'react-router-dom'
import { usePermission } from '@/lib/permissions'
import type { AuditLinkTarget } from '@/lib/auditLinks'
import { sameSource, TRAIL_PERMISSIONS, type TrailSource } from '@/lib/auditTrailSources'
import { useAuditTrailDialog } from './auditTrailDialogContext'

const LINK_CLASS = 'text-primary underline-offset-2 hover:underline focus-visible:underline'

/**
 * One reference in the audit trail, as a link when it can be followed.
 *
 * Three states, and the difference between them matters in a compliance
 * record: a resolvable target the reader may open becomes a link; a target
 * they may NOT open renders as plain text rather than a page that bounces
 * them; and a reference with no target at all renders exactly as it did
 * before this existed.
 *
 * The permission check is `canAny`, the same call the route guard makes
 * (`src/router/index.tsx`), against the permissions the target carries. It is
 * per tenant, so the same row can be linkable in one tenant and not another.
 *
 * Nothing here changes the stored row — this is display only. ClickUp
 * z8nrz7dxeu.
 *
 * A reference whose item has an audit trail of its own (`source`) opens THAT
 * trail in the audit pop-up instead of the item's page (z8nrz7dq6q); the page
 * stays reachable from the pop-up's "Open item". A link to the trail already on
 * screen is plain text — clicking it would only reload what the reader is
 * looking at.
 */
export function AuditLink({
  target,
  source = null,
  children,
  className,
  title,
}: {
  target: AuditLinkTarget | null
  /** The linked item's own audit trail, opened in the pop-up when present. */
  source?: TrailSource | null
  children: React.ReactNode
  className?: string
  title?: string
}) {
  const { canAny } = usePermission()
  const trail = useAuditTrailDialog()

  if (source && trail && canAny(TRAIL_PERMISSIONS[source.kind])) {
    if (sameSource(trail.current, source)) {
      return (
        <span className={className} title={title}>
          {children}
        </span>
      )
    }
    return (
      <button
        type='button'
        title={title ?? 'Open this item’s audit trail'}
        onClick={() => trail.open(source)}
        className={(className ? `${className} ` : '') + `text-left ${LINK_CLASS}`}
      >
        {children}
      </button>
    )
  }

  if (!target || !canAny(target.permissions)) {
    return (
      <span className={className} title={title}>
        {children}
      </span>
    )
  }

  return (
    <Link
      to={target.to}
      title={title}
      className={(className ? `${className} ` : '') + LINK_CLASS}
    >
      {children}
    </Link>
  )
}
